import { getAssetList } from '../../Config/Assets';
import { IGameSizeInfo, MapList, NODES_PER_MAP, setBoardData } from '../../Config/Levels';
import { AllMapChests } from '../../Config/MapChests';
import GameLoader from '../../Core/Assets';
import Core from '../../Core/Core';
import getLogger, { ILogger } from '../../Core/Logger';
import { ShareableMoments } from '../../SDK/SDKmessages';
import { Actions } from '../../State/Actions/Actions';
import { IStateLevel } from '../../State/reducers/level';
import { IStatePlayer } from '../../State/reducers/player';
import { Views } from '../../State/reducers/view';
import { IGameState, IStateSubcriber } from '../../State/Store/store';
import { waitForMs } from '../../Utils/AsyncUtils';
import { getCollectionProgress } from '../../Utils/CollectableUtils';
import { StateUtils } from '../StateUtils';
import { getBoardJson } from '../../Utils/Utils';
import MapView from '../../View/MapView';
import { isNewPlayer } from '../Game';
import { Hud } from '../Hud/Hud';
import { HudActions, HudEmitter } from '../Hud/HudEmitter';
import { Popup } from '../Popups';
import FTUETracking, { FTUE_TRACK_STAGES } from '../Tracking/FTUETracking';
import { MapActions, MapEmitter } from './MapEmitter';
import { Manager } from '../Manager';
import { MapProgression } from './MapProgression';
import { MapUtils } from './MapUtils';
import MapChests from './MapChests';
import { Platform } from '../../SDK/SDK';
import { LoadStages, Tracking } from '../Tracking/Tracking';
import { IStateSocial } from '../../State/socialHelpers';

const mapLogger: ILogger = getLogger('[MAP]:');

export default class MapManager extends Manager {
    public view: MapView;
    private hud: Hud;
    private giftListener: IStateSubcriber<any>;
    private nodeSelected: boolean = false;
    private addsListener: (type: Popup, layer: number) => void;
    private levelListener: (id: string) => void;
    private chests: MapChests;

    constructor(hud: Hud) {
        super();

        this.hud = hud;

        this.addSelector(
            (state: IStateLevel, pre: IStateLevel) => {
                this.handlePrelevel(state, pre);
            },
            (state: IGameState) => state.StateLevel
        );

        this.addsListener = (_, layer) => {
            if (layer && layer !== 0) {
                return;
            }
            this.showCurrAdds().catch(err => {
                mapLogger.error('show added currency error: ' + err);
            });
        };

        this.levelListener = (id: string) => {
            this.handleLevelSelect(id);
        };
        HudEmitter.instance.on(HudActions.POPUP_CLOSED, this.addsListener);
        HudEmitter.instance.on(HudActions.SHOW_CURR_ADDS, this.addsListener);
        MapEmitter.instance.on(MapActions.LEVEL_SELECT, this.levelListener);
    }

    public async init(mapLeaderBoard: any[], loadStart?: number): Promise<void> {
        const {
            StatePlayer: player,
            StateLevel: level,
            StateSocial: social,
        } = Core.store.getState();

        MapProgression.setProgression(player, level.levelIndex);

        const list = getAssetList(Views.MAP, player, isNewPlayer());
        await GameLoader.loadAssetList({
            assetList: list,
            onProgress: null,
            cb: null,
            oncomplete: () => {},
        });

        this.chests = new MapChests();

        if (loadStart) {
            const completeTime = Date.now() - loadStart;
            Tracking.trackLoadTime(LoadStages.MAP, completeTime);
        }

        this.view = new MapView(
            player.mapId,
            player.mapLevels,
            AllMapChests[player.mapId],
            player.unlockedChests
        );

        this.showFriendsOnMap(mapLeaderBoard, MapProgression.current.mapLastLevelId);

        this.initRest(player, social).catch(err => {
            console.log(['map initRest error', err]);
        });

        FTUETracking.track(FTUE_TRACK_STAGES.MAP_SHOWN, Core.store.getState());
        if (MapList.indexOf(player.mapId) === MapList.length - 1) {
            this.view.addComingSoon();
        }
    }

    /**
     * initializes everything else so the loading can already be completed
     * @param player: reference to the playerstate
     * @param social: reference to the socialstate
     */
    private async initRest(player: IStatePlayer, social: IStateSocial): Promise<void> {
        this.checkForGift();

        const {
            mapLastLevel,
            mapLastLevelId,
            startNodeIndex,
            showProgression,
        } = MapProgression.current;

        const avatarIndex = showProgression ? startNodeIndex : mapLastLevel;
        this.view.areaMap.addAvatar(Core.sdk.user.avatar, true, avatarIndex);
        if (!showProgression) {
            this.view.areaMap.setActiveNode(mapLastLevel);
        }
        this.view.centerOnPlayer(startNodeIndex);

        if (showProgression) {
            this.showPlayerProgress(mapLastLevel).catch(err => {
                mapLogger.debug('error with showing map progression: ' + err);
            });
        }

        if (MapUtils.checkMessages(social)) {
            this.handleMessages(social);
            this.completeVisit();
            return;
        }

        this.showCurrAdds()
            .then(async () => {
                await waitForMs(1100);
                if (showProgression) {
                    if (!this.nodeSelected && mapLastLevel < NODES_PER_MAP + 1) {
                        this.handleLevelSelect(mapLastLevelId);
                    }
                } else {
                    this.completeVisit();
                }
            })
            .catch(err => {
                mapLogger.debug('error with currency animations with progression: ' + err);
            });

        return;
    }

    public completeVisit(): void {
        this.checkForNewMap();
        this.unlockNeeded();
        Core.store.dispatch(Actions.visitMap(Core.store.getState().StatePlayer.mapId));
    }

    public unlockNeeded(): void {
        const { StatePlayer: player } = Core.store.getState();
        const { unlockNextMap, unlockNextLevel, mapLastLevelId } = MapProgression.current;
        if (unlockNextMap) {
            Core.store.dispatch(Actions.unlockMap(MapList[player.unlocked.length]));
        }

        if (unlockNextLevel) {
            Core.store.dispatch(Actions.unlockMapLevel(mapLastLevelId));
        }
    }

    public async showGainedStars(starIndex: number): Promise<void> {
        await this.view.areaMap.showNodeProgress(
            Core.store.getState().StatePlayer.gainedStars,
            starIndex
        );
    }

    public async showPlayerProgress(nodeIndex: number): Promise<void> {
        await this.view.areaMap.moveToNode(nodeIndex);
        this.view.areaMap.setActiveNode(nodeIndex);
    }

    public update(): void {
        if (this.view) {
            this.view.update();
        }
    }

    public cleanUp(): void {
        super.cleanUp();

        this.chests.cleanUp();
        this.chests = null;

        MapEmitter.instance.off(MapActions.LEVEL_SELECT, this.levelListener);
        HudEmitter.instance.off(HudActions.POPUP_CLOSED, this.addsListener);
        HudEmitter.instance.off(HudActions.SHOW_CURR_ADDS, this.addsListener);

        this.view.cleanUp();
        this.view.destroy();
        this.view = null;
    }

    public resize(gameSize: IGameSizeInfo): void {
        this.view.resize(gameSize);
    }

    public async showCurrAdds(): Promise<void> {
        if (!this.hud) {
            return;
        }
        const state = Core.store.getState();
        const statePlayer = state.StatePlayer;
        const stateCurrency = state.StateCurrency;

        const { mapLastPlayedLevel, mapLastLevel } = MapProgression.current;

        const {
            gainedBoosters,
            gainedCoins,
            gainedTokens,
            gainedHearts,
            gainedStars,
            totalStars,
            gainedTokensCollection,
        } = statePlayer;
        const { coin, heart } = stateCurrency;
        const totalTokens = getCollectionProgress(gainedTokensCollection);

        if (gainedStars !== undefined && gainedStars > 0) {
            const starIndex = mapLastPlayedLevel;
            await this.showGainedStars(starIndex);
        }

        Core.store.dispatch(Actions.receiveRewards());

        await this.hud.view.showCurrAds(
            totalStars,
            gainedStars,
            coin,
            gainedCoins,
            heart,
            gainedHearts,
            totalTokens,
            gainedTokens,
            gainedTokensCollection
        );

        if (gainedBoosters !== undefined && gainedBoosters.length > 0 && mapLastLevel < 50) {
            await this.view.showNodeRewards(mapLastLevel, gainedBoosters);
        }
    }

    private checkForGift(): void {
        if (Core.store.getState().StateSocial.gifts.length > 0) {
            HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.BOTREWARD);
        }

        this.giftListener = {
            callback: (state: IStateSocial, pre: IStateSocial) => {
                if (state.gifts.length > pre.gifts.length) {
                    HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.BOTREWARD);
                }
            },
            selector: (state: IGameState) => state.StateSocial,
            id: 'giftListener',
        };

        Core.store.addListener([this.giftListener]);
    }

    private async showFriendJoinedPopup(player: any[]): Promise<void> {
        HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.FRIEND_JOINED, player[0]);
    }

    private async showGiftReceivedPopup(friendId: string): Promise<void> {
        HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.GIFT_RECEIVED, friendId);
    }

    private async checkForNewMap() {
        await GameLoader.loadAssetList({ assetList: ['social', 'fx'] });
        const player = Core.store.getState().StatePlayer;
        if (StateUtils.newMapUnlocked(player)) {
            if (sgPlatform === Platform.Network) {
                const numUnlocked = Core.store.getState().StatePlayer.unlocked.length;
                const lastMap = Core.store.getState().StatePlayer.unlocked[numUnlocked - 1];
                Core.store.dispatch(Actions.changeMap(lastMap));
                Core.store.dispatch(Actions.changeView(Views.MAP));
            } else {
                const completedMap = player.unlocked[player.unlocked.length - 2];
                HudEmitter.instance.emit(
                    HudActions.ADD_POPUP,
                    Popup.SHAREABLE,
                    0,
                    ShareableMoments.NEW_MAP,
                    completedMap,
                    true
                );
            }
        }
    }

    private async handlePrelevel(state: IStateLevel, pre: IStateLevel): Promise<void> {
        if (state.prelevel !== pre.prelevel) {
            if (state.prelevel !== '') {
                const res = getBoardJson(state.prelevel);
                if (!res) {
                    return;
                }
                HudEmitter.instance.emit(HudActions.ADD_POPUP, Popup.PRELEVEL, 0, {
                    config: res.config,
                    prelevelId: state.prelevel,
                });
            }
        }
    }

    private async showFriendsOnMap(list: Array<unknown>, playerMapLevel: string): Promise<void> {
        const mapBaseMulitplier =
            MapList.indexOf(playerMapLevel.substr(0, playerMapLevel.indexOf('-'))) + 1;
        const levelBoard = MapUtils.convertMapLeaderBoardScores(list, mapBaseMulitplier);
        levelBoard.forEach((d: any) => {
            this.view.areaMap.addAvatar(d.user.avatarUrl, false, d.levelIndex);
        });

        return;
    }

    private async handleLevelSelect(id: string): Promise<void> {
        if (Core.DEBUG || Object.keys(Core.store.getState().StatePlayer.mapLevels).includes(id)) {
            this.nodeSelected = true;
            const res = getBoardJson(id);
            setBoardData(res);
            Core.store.dispatch(Actions.initLevel(id));
        }
    }

    private handleMessages(social: IStateSocial) {
        if (social.confirmedInvitations.length > 0) {
            this.showFriendJoinedPopup(social.confirmedInvitations).catch(err => {
                console.log(['error showFriendJoined', err]);
            });
        }
        if (social.giftFromFriend !== '') {
            this.showGiftReceivedPopup(social.giftFromFriend).catch(err => {
                console.log(['error showGift', err]);
            }); // friendId
            Core.store.dispatch(Actions.recordFriendGift());
        }
    }
}
